import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { FormAction } from '@anytag/shared-types';
import { GTM_IDS } from '@anytag/shared-constants';
import { MarketplaceDetail } from '@anytag/graphql';
import { getRestApiUrl } from '@anytag/shared-utils';
import { RadiosField } from '@anytag/shared-molecules';
import { useDownloadFile, useToggleState } from '@anytag/shared-hooks';
import { Form, DownloadButton, BaseModal, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { MarketplacePostsFilterFormValues } from './schemaTypes';

enum DownloadType {
  COMMENT = 'COMMENT',
  INFLUENCER = 'INFLUENCER'
}

interface DownloadMarketplacePostsButtonProps {
  marketplaceId: MarketplaceDetail['id'];
}

export const DownloadMarketplacePostsButton = ({ marketplaceId }: DownloadMarketplacePostsButtonProps) => {
  const { t } = useTranslation();
  const modalState = useToggleState();
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { getValues } = useFormContext<MarketplacePostsFilterFormValues>();

  const values = getValues();

  const handleDownload: FormAction<{ type: DownloadType }>['onSubmit'] = async ({ type }) => {
    await handleDownloadFile(
      type === DownloadType.INFLUENCER
        ? {
            params: values,
            filenamePrefix: 'influencer_list',
            url: getRestApiUrl(`marketplace/${marketplaceId}/post/csv/`)
          }
        : {
            params: values,
            filenamePrefix: 'post_comments',
            url: getRestApiUrl(`marketplace/${marketplaceId}/posts/comments/`)
          }
    );
    modalState.close();
  };

  return (
    <>
      <Form.Field className="flex-none">
        <DownloadButton
          title=""
          onClick={modalState.open}
          id={GTM_IDS.marketplace.posts_csv_download}
          tooltip={{ help: t('Button.CSV Download') }}
        />
      </Form.Field>

      <BaseModal
        open={modalState.status}
        onClose={modalState.close}
        title={t('Dialog.Export')}
        description={t('Dialog.Select type of information you wish to include in exported csv file')}
        asChild
      >
        <Form.Provider<{ type: DownloadType }>
          onSubmit={handleDownload}
          defaultValues={{ type: DownloadType.INFLUENCER }}
        >
          <ModalBody>
            <RadiosField
              name="type"
              direction="vertical"
              options={[
                { label: t('Influencer list'), value: DownloadType.INFLUENCER },
                { label: t('Post comment list'), value: DownloadType.COMMENT }
              ]}
            />
          </ModalBody>

          <ModalFooter
            cancelButton={{ onClick: modalState.close }}
            submitButton={{ type: 'submit', loading: downloading, title: t('Button.Submit') }}
          />
        </Form.Provider>
      </BaseModal>
    </>
  );
};
